// src/features/feeds/useFeedMutations.ts

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { createBatchMutationHook } from '@/shared/hooks/useBatchMutation';
import { FEED_QUERY_KEYS } from '@/lib/constants/feed';
import { FeedApi } from './FeedApi';
import type { FeedCreateRequest, FeedData } from '@/lib/types/Feed';

// Batch mutations via shared factory
export const useActivateFeeds = createBatchMutationHook(
  FeedApi.activateFeeds,
  (cId) => FEED_QUERY_KEYS.list(cId),
);

export const useDeactivateFeeds = createBatchMutationHook(
  FeedApi.deactivateFeeds,
  (cId) => FEED_QUERY_KEYS.list(cId),
);

export const useDeleteFeeds = createBatchMutationHook(
  FeedApi.deleteFeeds,
  (cId) => FEED_QUERY_KEYS.list(cId),
);

// --- Detail-level mutations ---

/** Create a new feed. */
export function useCreateFeed() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: FeedCreateRequest) => FeedApi.createFeed(selectedCId!, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FEED_QUERY_KEYS.list(selectedCId!) });
    },
  });
}

/** Update an existing feed (detail page save). */
export function useUpdateFeed(feedId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: FeedCreateRequest) => FeedApi.updateFeed(selectedCId!, feedId, req),
    onSuccess: (updatedFeed) => {
      const listKey = FEED_QUERY_KEYS.list(selectedCId!);
      // Optimistically replace the updated feed within the list cache
      queryClient.setQueryData<FeedData[]>(listKey, (prev) =>
        prev?.map((f) => (f.feedId === feedId ? updatedFeed : f)),
      );
      queryClient.invalidateQueries({ queryKey: listKey });
    },
  });
}
